import { useState } from "react";
import { fmtUsd, timeAgo } from "./nl";

/* Yield Intelligence — qualifying live pool set (Figma contract).
   Communicates pool/asset, chain, APY and TVL only — information,
   not yield recommendation. Section hides when no qualifying pools. */

export const YieldIntelligence = ({ overview }: { overview: any }) => {
  const [expanded, setExpanded] = useState(false);
  const all = overview.yields?.pools || [];
  const pools = expanded ? all : all.slice(0, 6);
  if (!pools.length) return null;
  const truncated = all.length > 6;
  const name = overview.identity.name;
  return (
    <section data-testid="yield-intelligence">
      <p className="nl-geist text-[9px] md:text-[10px] lg:text-[11px] leading-[14px] font-semibold text-[#7a3dff] tracking-[0.02em]">PROTOCOL ACTIVITY</p>
      <h2 className="nl-sg text-[27px] leading-[35px] md:text-[31px] md:leading-[40px] lg:text-[34px] lg:leading-[43px] font-medium text-[#f6f7fc] mt-[8px]">
        Yield Intelligence
      </h2>
      <p className="nl-geist text-[12px] md:text-[14px] lg:text-[15px] leading-[16px] md:leading-[19px] text-[#9499ad] mt-[6px] max-w-[720px]">
        Largest relevant {name} pools by TVL — information, not yield recommendation.
      </p>
      <div className="mt-[22px] rounded-[20px] md:rounded-[24px] lg:rounded-[26px] bg-[#050508] border border-[#1c1f2e] p-[12px] md:p-[18px] lg:px-[28px] lg:py-[24px]">
        {/* Desktop table */}
        <div className="hidden md:block">
          <div className="grid grid-cols-[minmax(0,2.6fr)_1.6fr_1.2fr_1.2fr] gap-[12px] pb-[10px]">
            {["POOL / ASSET", "CHAIN", "APY", "TVL"].map((h) => (
              <span key={h} className="nl-geist text-[10px] leading-[13px] font-semibold text-[color:var(--nl-text-faint)]">{h}</span>
            ))}
          </div>
          {pools.map((p: any, i: number) => (
            <div key={`${p.symbol}-${p.chain}-${i}`}
              className="grid grid-cols-[minmax(0,2.6fr)_1.6fr_1.2fr_1.2fr] gap-[12px] items-center border-t border-[#12141f] py-[18px]"
              data-testid={`yield-row-${i}`}>
              <div className="min-w-0">
                <p className="nl-geist text-[18px] leading-[23px] font-semibold text-[#f6f7fc] truncate">{p.symbol}</p>
                <p className="nl-geist text-[11px] leading-[14px] text-[color:var(--nl-text-faint)] mt-[2px]">{name} pool</p>
              </div>
              <p className="nl-geist text-[14px] leading-[18px] font-medium text-[#9499ad] truncate">{p.chain}</p>
              <p className="nl-sg text-[22px] leading-[28px] font-medium"
                style={{ color: p.apy_pct != null && p.apy_pct > 0 ? "#1fe09c" : "#9499ad" }}>
                {p.apy_pct != null ? `${p.apy_pct.toFixed(2)}%` : "—"}
              </p>
              <p className="nl-geist text-[18px] leading-[23px] font-medium text-[#f6f7fc]">{fmtUsd(p.tvl_usd)}</p>
            </div>
          ))}
        </div>
        {/* Mobile stacked cards */}
        <div className="md:hidden space-y-[12px]">
          {pools.map((p: any, i: number) => (
            <div key={`${p.symbol}-${p.chain}-m-${i}`}
              className="rounded-[16px] bg-[#060609] border border-[#141724] p-[14px]"
              data-testid={`yield-card-${i}`}>
              <p className="nl-geist text-[18px] leading-[23px] font-semibold text-[#f6f7fc]">{p.symbol}</p>
              <p className="nl-geist text-[10px] leading-[13px] text-[color:var(--nl-text-faint)] mt-[2px]">{name} pool · {p.chain}</p>
              <div className="flex gap-[56px] mt-[16px]">
                <div>
                  <p className="nl-sg text-[22px] leading-[28px] font-medium"
                    style={{ color: p.apy_pct != null && p.apy_pct > 0 ? "#1fe09c" : "#9499ad" }}>
                    {p.apy_pct != null ? `${p.apy_pct.toFixed(2)}%` : "—"}
                  </p>
                  <p className="nl-mono text-[8px] leading-[10px] font-medium text-[color:var(--nl-text-faint)] mt-[2px]">APY</p>
                </div>
                <div>
                  <p className="nl-geist text-[18px] leading-[23px] font-medium text-[#f6f7fc]">{fmtUsd(p.tvl_usd)}</p>
                  <p className="nl-mono text-[8px] leading-[10px] font-medium text-[color:var(--nl-text-faint)] mt-[2px]">TVL</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-[16px] md:mt-[18px] flex flex-wrap items-center justify-between gap-[10px] px-[2px]">
          <p className="nl-mono text-[7px] md:text-[9px] leading-[12px] font-medium text-[color:var(--nl-text-faint)] uppercase"
            data-testid="yield-provenance">
            {truncated && !expanded ? `SHOWING ${pools.length} OF ${all.length} QUALIFYING POOLS · ` : ""}QUALIFYING LIVE POOL SET · SOURCE · DEFILLAMA YIELDS · {timeAgo(overview.yields?.fetched_at) || "LIVE"}
          </p>
          {truncated && (
            <button type="button" onClick={() => setExpanded((v) => !v)}
              className="nl-geist text-[9px] md:text-[10px] leading-[14px] font-semibold text-[#8b5cff] hover:text-white transition-colors duration-200"
              data-testid="yield-view-all-toggle">
              {expanded ? "SHOW FEWER" : `VIEW ALL ${all.length} →`}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};
